import { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Camera, CheckCircle2, AlertTriangle, FolderKanban, Info,
  ChevronRight, ChevronLeft, Plus, X, Hash, Tag, Trash2,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import {
  Card, Button, Field, Input, Textarea, Spinner,
} from "@/components/ui";
import { StickyBottomBar } from "@/components/layout/StickyBottomBar";
import { loadDesignBundle, designDigest } from "@/features/projects/DesignPanel";

// =============================================================================
// HopNewPackScreen — /projects/:projectId/hop/new (5-step wizard)
//
// T-010 HOP Commit C.3b. Creates a hop_packs row + pack-owned categories +
// pack-owned subcategories + per-instance rows in a single submit.
//
// Steps:
//   1. Template — pick CUSTOM / HVAC / NBN
//   2. Scope + qty — tick categories, edit display name + required_qty per row,
//      add custom categories
//   3. Instances — for selected per-instance categories (Sector / Microwave /
//      Active Antenna), declare instance codes (S1, S2, S3 / MW1 facing X)
//   4. Label prefix — site code, drives export filenames
//   5. Review + create — summary then commit
//
// Manager-only (RLS also enforces). Uses sequential Supabase inserts for the
// commit step; unique constraints on (pack_id, code) etc. make retries
// idempotent if a network blip splits the transaction.
// =============================================================================

type Step = 1 | 2 | 3 | 4 | 5;

// T-283 — Cabinet-class categories (cabinet / rbs_cabinet / shelter /
// bbs_cabinet) added alongside the original sector/antenna/microwave set.
// Sites can legitimately have multiple of each of these, and Ventia needs
// the identifier (e.g. BBS001, RBS01, SHEL1) carried through to the
// submission filename so they can tell which cabinet/shelter each photo
// belongs to. Previously only sectors/antennas/MW got that treatment;
// cabinets fell through to the "add extra subcategories" path which created
// rogue rows like "BBS 1" / "BBS 2" inside the single category instead of
// proper per-instance structure. Codes match migration 041 NBN template seed.
const PER_INSTANCE_CODES = new Set([
  "sector",
  "active_antenna",
  "microwave",
  "cabinet",
  "rbs_cabinet",
  "shelter",
  "bbs_cabinet",
]);

// T-411 — NBN guidelines: cabinet-family instance codes are FIXED sequences
// (BBS001, BBS002 / CAB001 / RBS001 / SHEL001), never free text. Only
// sectors, active antennas and MW links carry typed identifiers (S1, B3,
// far-end link name). These categories get auto-generated, non-editable
// codes; qty on step 2 drives how many rows appear.
const AUTO_CODE_PREFIX: Record<string, string> = {
  bbs_cabinet: "BBS",
  cabinet:     "CAB",
  rbs_cabinet: "RBS",
  shelter:     "SHEL",
};
const autoCode = (prefix: string, i: number) => `${prefix}${String(i + 1).padStart(3, "0")}`;

interface TemplateOption {
  template_id:   string;
  template_code: string;
  display_name:  string;
  description:   string | null;
  sort_order:    number;
  categoryCount: number;
}

interface TemplateCategory {
  category_id:  string;
  template_id:  string;
  code:         string;
  display_name: string;
  description:  string | null;
  required_qty: number;
  sort_order:   number;
}

interface SelectedCategory {
  // null source = pack-specific custom category
  source_template_category_id: string | null;
  code:          string;        // unique within the pack
  display_name:  string;        // editable
  description:   string | null;
  required_qty:  number;        // editable
  sort_order:    number;
  is_custom:     boolean;
  is_per_instance: boolean;
}

interface InstanceRow {
  instance_code:  string;       // e.g. S1
  instance_label: string;       // e.g. "Sector S1 (north face)"
}

interface ProjectMini {
  project_id:   string;
  project_code: string;
  project_name: string;
  site_code:    string | null;  // used as default label prefix
}

// -----------------------------------------------------------------------------

export default function HopNewPackScreen() {
  const { projectId } = useParams<{ projectId: string }>();
  const user = useRequireAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canCreate = hasRole(user, "manager");

  const [step, setStep] = useState<Step>(1);
  const [templateId, setTemplateId] = useState<string>("");
  const [selected, setSelected] = useState<Map<string, SelectedCategory>>(new Map());
  const [instances, setInstances] = useState<Record<string, InstanceRow[]>>({});
  const [labelPrefix, setLabelPrefix] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [err, setErr] = useState<string | null>(null);

  // -- Load project + templates -------------------------------------------
  const { data: project } = useQuery({
    queryKey: ["hop_new_project_mini", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("project_id, project_code, project_name, sites(site_code)")
        .eq("project_id", projectId!)
        .maybeSingle();
      if (error) throw error;
      const sites = (data as any)?.sites;
      return {
        project_id: data!.project_id,
        project_code: data!.project_code,
        project_name: data!.project_name,
        site_code: sites?.site_code ?? null,
      } as ProjectMini;
    },
  });

  // Suggest a sensible default for the label prefix once project loads.
  useEffect(() => {
    if (!labelPrefix && project) {
      const fallback = project.site_code || project.project_code;
      if (fallback) setLabelPrefix(fallback);
    }
  }, [project, labelPrefix]);

  const { data: templates = [], isLoading: loadingTemplates } = useQuery({
    queryKey: ["hop_template_options"],
    queryFn: async () => {
      const { data: tpls, error: tErr } = await supabase
        .from("hop_photo_templates")
        .select("template_id, template_code, display_name, description, sort_order")
        .eq("is_active", true)
        .order("sort_order");
      if (tErr) throw tErr;
      const { data: cats, error: cErr } = await supabase
        .from("hop_photo_template_categories")
        .select("template_id");
      if (cErr) throw cErr;
      const counts = new Map<string, number>();
      for (const c of cats ?? []) {
        counts.set(c.template_id, (counts.get(c.template_id) ?? 0) + 1);
      }
      return (tpls ?? []).map(t => ({
        template_id:   t.template_id,
        template_code: t.template_code,
        display_name:  t.display_name,
        description:   t.description,
        sort_order:    t.sort_order,
        categoryCount: counts.get(t.template_id) ?? 0,
      })) as TemplateOption[];
    },
  });

  // Categories for the selected template
  const { data: templateCategories = [], isLoading: loadingCats } = useQuery({
    queryKey: ["hop_template_cats", templateId],
    enabled: !!templateId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hop_photo_template_categories")
        .select("category_id, template_id, code, display_name, description, required_qty, sort_order")
        .eq("template_id", templateId)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as TemplateCategory[];
    },
  });

  // When the template changes, prime selected with all template categories
  // already ticked. Manager unticks the ones that don't apply rather than
  // ticking from a blank slate — much faster for NBN's 18 categories.
  useEffect(() => {
    if (!templateId || templateCategories.length === 0) return;
    const next = new Map<string, SelectedCategory>();
    for (const c of templateCategories) {
      next.set(c.code, {
        source_template_category_id: c.category_id,
        code: c.code,
        display_name: c.display_name,
        description: c.description,
        required_qty: c.required_qty,
        sort_order: c.sort_order,
        is_custom: false,
        is_per_instance: PER_INSTANCE_CODES.has(c.code),
      });
    }
    setSelected(next);
    // Reset instance rows when template changes
    setInstances({});
  }, [templateId, templateCategories]);

  // -- Derived state ------------------------------------------------------
  const selectedArray = useMemo(
    () => Array.from(selected.values()).sort((a, b) => a.sort_order - b.sort_order),
    [selected],
  );
  const perInstanceSelected = selectedArray.filter(c => c.is_per_instance);
  // T-088 fix: Step 3 now shows for ANY category that needs labels — strict
  // per-instance ones (Sector/MW/AA — must declare codes like S1, S2) AND
  // any category with required_qty > 1 (so a "General" pack with qty=5 can
  // optionally label each photo slot like "Site overview", "Pole base", etc).
  const labelableCategories = selectedArray.filter(c => c.is_per_instance || c.required_qty > 1);
  const hasPerInstance = labelableCategories.length > 0;

  // Auto-seed instances when entering Step 3 — pre-create empty rows up to
  // the required_qty so the user sees the right number of slots immediately.
  // Idempotent: only fills categories with no rows yet.
  useEffect(() => {
    if (!hasPerInstance) return;
    setInstances(prev => {
      const next = { ...prev };
      let changed = false;
      for (const c of labelableCategories) {
        const target = Math.max(1, c.required_qty || 1);
        const rows = next[c.code] ?? [];
        const prefix = AUTO_CODE_PREFIX[c.code];
        // T-411 — TOP UP to the step-2 qty (previously only seeded when
        // empty, so "qty 2" typed after first render still showed 1 row).
        // Never removes rows — the X button covers over-seeding.
        if (rows.length < target) {
          const added = Array.from({ length: target - rows.length }, (_, k) => ({
            instance_code: prefix ? autoCode(prefix, rows.length + k) : "",
            instance_label: "",
          }));
          next[c.code] = [...rows, ...added];
          changed = true;
        } else if (prefix && rows.length > 0 && rows.every(r => !r.instance_code.trim())) {
          // Rows seeded before this fix carry blank codes — backfill the sequence.
          next[c.code] = rows.map((r, k) => ({ ...r, instance_code: autoCode(prefix, k) }));
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [hasPerInstance, labelableCategories.map(c => `${c.code}:${c.required_qty}`).join("|")]);

  // -- Site design pre-fill (T-SCOPE-3, 1 Sep 2026) ------------------------
  // James: "get the design into the HOP when creating it - link name and far
  // end, sector count and naming, cabinet qty - these play into how we create
  // the HOP." One click sets the per-instance quantities AND the instance
  // codes/labels (S1/S2/S3, "uWave facing <far end>") from the Design tab.
  const { data: designBundle } = useQuery({
    queryKey: ["design_bundle", projectId ?? null, null],
    enabled: !!projectId,
    queryFn: () => loadDesignBundle({ projectId }),
  });
  const digest = designBundle?.design
    ? designDigest(designBundle, [project?.site_code, project?.project_name, project?.project_code])
    : null;
  const [designApplied, setDesignApplied] = useState(false);

  function applyDesignCounts() {
    if (!digest) return;
    const counts: Record<string, number> = {
      sector: digest.sectors.length,
      active_antenna: digest.activeAntennaCount,
      microwave: digest.mwInstances.length,
      cabinet: digest.cabinetCount,
      rbs_cabinet: digest.rbsCount,
      shelter: digest.shelterCount,
    };
    const next = new Map(selected);
    for (const [code, qty] of Object.entries(counts)) {
      const c = next.get(code);
      if (c && qty > 0) next.set(code, { ...c, required_qty: qty });
    }
    setSelected(next);
    // Pre-name the instances the design already names. Sectors use the
    // design's own sector ids; MW links get the facing-site convention the
    // NBN photo spec wants. Cabinet-family codes stay auto (BBS001...).
    setInstances(prev => {
      const nx = { ...prev };
      if (next.has("sector") && digest.sectors.length) {
        nx["sector"] = digest.sectors.map(s => ({ instance_code: s, instance_label: "" }));
      }
      if (next.has("microwave") && digest.mwInstances.length) {
        nx["microwave"] = digest.mwInstances.map((mi, i) => ({
          instance_code: `MW${i + 1}`,
          instance_label: mi.label,
        }));
      }
      return nx;
    });
    setDesignApplied(true);
  }

  // -- Step navigation ----------------------------------------------------
  const visibleSteps: Step[] = hasPerInstance ? [1, 2, 3, 4, 5] : [1, 2, 4, 5];
  const stepLabels: Record<Step, string> = {
    1: "Template",
    2: "Scope + qty",
    3: "Instances",
    4: "Label + title",
    5: "Review",
  };

  function goNext() {
    setErr(null);
    if (step === 1 && !templateId) { setErr("Pick a template to base this pack on."); return; }
    if (step === 2 && selected.size === 0) { setErr("Select at least one category for this pack."); return; }
    if (step === 3) {
      // T-088: For strict per-instance categories (Sector/MW/AA), codes are
      // required (S1, S2, MW1...). For other qty>1 categories (e.g. General
      // with 5 photos), codes are optional — we'll auto-fill 01, 02, 03 etc.
      // Labels are always optional everywhere.
      for (const c of labelableCategories) {
        const rows = instances[c.code] ?? [];
        if (rows.length === 0) {
          setErr(`Add at least one instance to "${c.display_name}".`); return;
        }
        if (c.is_per_instance && rows.some(r => !r.instance_code.trim())) {
          setErr(`Every instance under "${c.display_name}" needs a code (e.g. S1, S2).`); return;
        }
      }
    }
    if (step === 4 && !labelPrefix.trim()) {
      setErr("Label prefix is required — drives export filenames.");
      return;
    }
    const idx = visibleSteps.indexOf(step);
    if (idx < visibleSteps.length - 1) setStep(visibleSteps[idx + 1]);
  }
  function goBack() {
    setErr(null);
    const idx = visibleSteps.indexOf(step);
    if (idx > 0) setStep(visibleSteps[idx - 1]);
  }

  // -- Mutations ----------------------------------------------------------
  // T-413 — creation is now ONE atomic RPC (app.create_hop_pack, mig 237).
  // The old five-insert client-side flow stranded orphan pack shells on any
  // failure (the Nullarbor Pack 5 saga: 7 empty packs) and let wizard label
  // codes collide with template subcategory codes. Server-side: single
  // transaction, per-category code registry across BOTH sources, duplicates
  // auto-suffix instead of aborting.
  const createMut = useMutation({
    mutationFn: async () => {
      if (!projectId || !user?.user_id || !templateId) throw new Error("Missing project / template");
      if (!labelPrefix.trim()) throw new Error("Label prefix required");

      const payload = {
        project_id:  projectId,
        template_id: templateId,
        title:       title.trim() || null,
        notes:       notes.trim() || null,
        label_prefix: labelPrefix.trim(),
        categories: selectedArray.map(c => {
          const rows = (instances[c.code] ?? [])
            .map(r => ({ code: r.instance_code.trim(), label: r.instance_label.trim() }))
            .filter(r => r.code || r.label || c.is_per_instance === false);
          return {
            code:         c.code,
            display_name: c.display_name,
            description:  c.description,
            required_qty: c.required_qty,
            sort_order:   c.sort_order,
            is_custom:    c.is_custom,
            source_template_category_id: c.source_template_category_id ?? null,
            instances: c.is_per_instance ? rows.filter(r => r.code) : [],
            labels:    c.is_per_instance ? [] : rows,
          };
        }),
      };

      const { data: newPackId, error: rpcErr } = await supabase
        .schema("app")
        .rpc("create_hop_pack", { p_payload: payload });
      if (rpcErr) {
        const parts = [rpcErr.message, (rpcErr as any).details, (rpcErr as any).hint].filter(Boolean);
        throw new Error(parts.join(" — "));
      }
      if (!newPackId) throw new Error("Pack creation returned no id");
      return { pack_id: newPackId as string };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["hop_packs", projectId] });
      qc.invalidateQueries({ queryKey: ["field_hop_packs", projectId] });
      navigate(`/projects/${projectId}/hop/${data.pack_id}`);
    },
    onError: (e: any) => setErr(e?.message ?? "Failed to create pack"),
  });


  if (!user) return null;

  if (!canCreate) {
    return (
      <div className="p-6 max-w-xl mx-auto">
        <Card>
          <div className="p-10 text-center">
            <AlertTriangle size={32} className="mx-auto text-warn mb-3" />
            <div className="text-text font-medium">Manager access only</div>
            <div className="text-text-muted text-[13px] mt-1">
              Creating HOP packs is a manager / director task.
            </div>
            <div className="mt-4">
              <Link to={`/projects/${projectId}/hop`}>
                <Button variant="secondary">Back to HOP library</Button>
              </Link>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto pb-32">
      <button
        onClick={() => navigate(`/projects/${projectId}/hop`)}
        className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3"
        type="button"
      >
        <ArrowLeft size={14} /> HOP library
      </button>

      <div className="mb-5">
        {project && (
          <div className="text-[11px] font-mono text-text-muted">
            {project.project_code} · {project.project_name}
          </div>
        )}
        <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight flex items-center gap-2">
          <Camera size={20} className="text-accent" />
          New HOP pack
        </h1>
      </div>

      {/* Step indicator */}
      <div className="mb-5 flex items-center gap-1 overflow-x-auto">
        {visibleSteps.map((s, i) => {
          const active = s === step;
          const done = visibleSteps.indexOf(step) > i;
          return (
            <div key={s} className="flex items-center flex-shrink-0">
              <button
                type="button"
                onClick={() => { if (done || s < step) setStep(s); }}
                className={`flex items-center gap-2 px-3 py-1.5 rounded text-[12px] transition-colors ${
                  active
                    ? "bg-accent/15 text-accent border border-accent/30"
                    : done
                      ? "text-text hover:bg-ink-hover cursor-pointer"
                      : "text-text-muted"
                }`}
              >
                <span className={`w-5 h-5 rounded-full text-[10px] font-mono flex items-center justify-center ${
                  active ? "bg-accent text-ink" : done ? "bg-ok/20 text-ok" : "bg-ink-raised text-text-muted border border-line"
                }`}>
                  {done ? <CheckCircle2 size={11}/> : visibleSteps.indexOf(s) + 1}
                </span>
                {stepLabels[s]}
              </button>
              {i < visibleSteps.length - 1 && (
                <ChevronRight size={12} className="text-text-dim mx-0.5" />
              )}
            </div>
          );
        })}
      </div>

      {/* Step bodies */}
      {step === 1 && (
        <Step1Template
          loading={loadingTemplates}
          templates={templates}
          templateId={templateId}
          setTemplateId={setTemplateId}
        />
      )}
      {step === 2 && digest && (
        <div className={`mb-4 rounded-lg border px-4 py-3 ${designApplied ? "border-ok/50 bg-ok/5" : "border-accent/50 bg-accent/5"}`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[12px] text-text">
              <span className="font-medium">Site design found</span>
              <span className="text-text-muted">
                {" — "}{digest.sectors.length} sector{digest.sectors.length === 1 ? "" : "s"}
                {digest.sectors.length ? ` (${digest.sectors.join(", ")})` : ""}
                {digest.activeAntennaCount ? ` · ${digest.activeAntennaCount} active antennas` : ""}
                {digest.mwInstances.length ? ` · ${digest.mwInstances.length} MW dish${digest.mwInstances.length === 1 ? "" : "es"} (${digest.mwInstances.map(mi => mi.label).join(", ")})` : ""}
                {(digest.cabinetCount + digest.rbsCount + digest.shelterCount) ? ` · ${digest.cabinetCount + digest.rbsCount + digest.shelterCount} cabinet/shelter` : ""}
              </span>
            </div>
            {designApplied ? (
              <span className="text-[11px] text-ok">Quantities and instance names filled from the design — adjust below if needed.</span>
            ) : (
              <button type="button" onClick={applyDesignCounts}
                className="text-[12px] font-medium rounded-md bg-accent text-white px-3 py-1.5 hover:opacity-90">
                Use design counts
              </button>
            )}
          </div>
        </div>
      )}
      {step === 2 && (
        <Step2Scope
          loading={loadingCats}
          selected={selected}
          setSelected={setSelected}
        />
      )}
      {step === 3 && hasPerInstance && (
        <Step3Instances
          perInstance={labelableCategories}
          instances={instances}
          setInstances={setInstances}
        />
      )}
      {step === 4 && (
        <Step4LabelTitle
          labelPrefix={labelPrefix}
          setLabelPrefix={setLabelPrefix}
          title={title}
          setTitle={setTitle}
          notes={notes}
          setNotes={setNotes}
          firstSubcategoryCode={selectedArray[0]?.code ?? "Construction"}
        />
      )}
      {step === 5 && (
        <Step5Review
          template={templates.find(t => t.template_id === templateId) ?? null}
          selected={selectedArray}
          instances={instances}
          labelPrefix={labelPrefix}
          title={title}
          notes={notes}
        />
      )}

      {/* Sticky action bar */}
      <StickyBottomBar>
        {err && (
          <div className="px-3 py-2 bg-red-900/30 border-b border-red-900/50 text-red-200 text-[12px] text-center flex items-center justify-center gap-1.5">
            <AlertTriangle size={12} />
            {err}
          </div>
        )}
        <div className="p-3 flex gap-2 max-w-3xl mx-auto">
          <Button
            variant="secondary"
            size="lg"
            onClick={() => navigate(`/projects/${projectId}/hop`)}
            type="button"
          >
            Cancel
          </Button>
          {step !== visibleSteps[0] && (
            <Button variant="secondary" size="lg" icon={ChevronLeft} onClick={goBack} type="button">
              Back
            </Button>
          )}
          {step !== visibleSteps[visibleSteps.length - 1] ? (
            <Button size="lg" onClick={goNext} className="flex-1 justify-center" type="button">
              Next <ChevronRight size={14} />
            </Button>
          ) : (
            <Button
              size="lg"
              icon={CheckCircle2}
              onClick={() => createMut.mutate()}
              disabled={createMut.isPending}
              className="flex-1 justify-center"
              type="button"
            >
              {createMut.isPending ? "Creating…" : "Create pack"}
            </Button>
          )}
        </div>
      </StickyBottomBar>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Step 1 — Template picker
// -----------------------------------------------------------------------------
function Step1Template({
  loading, templates, templateId, setTemplateId,
}: {
  loading: boolean;
  templates: TemplateOption[];
  templateId: string;
  setTemplateId: (s: string) => void;
}) {
  return (
    <Card>
      <div className="p-5">
        <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono mb-3">
          1. Pick a template
        </div>
        {loading ? (
          <div className="py-10 flex justify-center"><Spinner size={18} /></div>
        ) : templates.length === 0 ? (
          <div className="text-text-muted text-[13px]">
            No templates available. Ask a director to seed one.
          </div>
        ) : (
          <div className="space-y-2">
            {templates.map(t => {
              const active = t.template_id === templateId;
              return (
                <button
                  key={t.template_id}
                  type="button"
                  onClick={() => setTemplateId(t.template_id)}
                  className={`w-full text-left p-4 rounded-md border transition-colors ${
                    active
                      ? "bg-accent/10 border-accent"
                      : "bg-ink-raised border-line hover:border-line-light"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[11px] font-mono tracking-wider2 uppercase ${active ? "text-accent" : "text-text-muted"}`}>
                          {t.template_code}
                        </span>
                        <span className="text-text font-medium">{t.display_name}</span>
                      </div>
                      {t.description && (
                        <div className="text-[12px] text-text-muted mt-1 line-clamp-3">{t.description}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0 text-[11px] text-text-muted font-mono">
                      <FolderKanban size={11} />
                      {t.categoryCount}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Step 2 — Scope + qty (checkbox grid + custom rows)
// -----------------------------------------------------------------------------
function Step2Scope({
  loading, selected, setSelected,
}: {
  loading: boolean;
  selected: Map<string, SelectedCategory>;
  setSelected: (m: Map<string, SelectedCategory>) => void;
}) {
  function toggle(c: SelectedCategory) {
    const next = new Map(selected);
    if (next.has(c.code)) next.delete(c.code);
    else next.set(c.code, c);
    setSelected(next);
  }

  function updateField(code: string, patch: Partial<SelectedCategory>) {
    const next = new Map(selected);
    const existing = next.get(code);
    if (existing) {
      next.set(code, { ...existing, ...patch });
      setSelected(next);
    }
  }

  function removeCategory(code: string) {
    const next = new Map(selected);
    next.delete(code);
    setSelected(next);
  }

  function addCustom() {
    const next = new Map(selected);
    let n = 1;
    while (next.has(`custom_${n}`)) n++;
    const code = `custom_${n}`;
    next.set(code, {
      source_template_category_id: null,
      code,
      display_name: "Custom category",
      description: null,
      required_qty: 1,
      sort_order: 1000 + n,
      is_custom: true,
      is_per_instance: false,
    });
    setSelected(next);
  }

  // We need to render BOTH ticked and unticked template categories so the
  // manager can untick. Pull from the template-category list instead of just
  // the selected set. The parent has primed `selected` with everything when
  // template changes — unticking a row removes from the map.
  const ordered = Array.from(selected.values()).sort((a, b) => a.sort_order - b.sort_order);
  // Customs at the bottom; everything from template above
  const fromTemplate = ordered.filter(c => !c.is_custom);
  const customs = ordered.filter(c => c.is_custom);

  if (loading) return <div className="py-10 flex justify-center"><Spinner size={18} /></div>;

  return (
    <div className="space-y-4">
      <Card>
        <div className="p-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono">
              2. Scope this pack — tick what applies + set quantity
            </div>
            <div className="text-[11px] text-text-muted font-mono">
              {selected.size} selected
            </div>
          </div>

          {fromTemplate.length === 0 ? (
            <div className="text-text-muted text-[13px] italic">
              No template categories selected. Use "+ Add custom category" below or go back and pick a different template.
            </div>
          ) : (
            <div className="space-y-2">
              {fromTemplate.map(c => (
                <CategoryRow
                  key={c.code}
                  cat={c}
                  ticked
                  onToggle={() => removeCategory(c.code)}
                  onChange={(patch) => updateField(c.code, patch)}
                  onRemove={null}
                />
              ))}
            </div>
          )}
        </div>
      </Card>

      {customs.length > 0 && (
        <Card>
          <div className="p-5">
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono mb-3">
              Custom categories
            </div>
            <div className="space-y-2">
              {customs.map(c => (
                <CategoryRow
                  key={c.code}
                  cat={c}
                  ticked
                  onToggle={null}
                  onChange={(patch) => updateField(c.code, patch)}
                  onRemove={() => removeCategory(c.code)}
                />
              ))}
            </div>
          </div>
        </Card>
      )}

      <div>
        <Button variant="secondary" icon={Plus} onClick={addCustom} type="button">
          Add custom category
        </Button>
      </div>

      <div className="p-3 rounded bg-info/10 border border-info/30 text-[12px] text-info flex items-start gap-2">
        <Info size={13} className="mt-0.5 flex-shrink-0" />
        <div>
          Categories like <span className="font-mono">Sector</span>, <span className="font-mono">Microwave</span>, <span className="font-mono">Active Antenna</span> expand per instance — you'll declare instance codes (S1, S2, MW1…) on the next step.
        </div>
      </div>
    </div>
  );
}

function CategoryRow({
  cat, ticked, onToggle, onChange, onRemove,
}: {
  cat: SelectedCategory;
  ticked: boolean;
  onToggle: (() => void) | null;
  onChange: (patch: Partial<SelectedCategory>) => void;
  onRemove: (() => void) | null;
}) {
  return (
    <div className={`p-3 rounded-md border ${ticked ? "bg-ink-raised border-line" : "bg-transparent border-line opacity-60"}`}>
      <div className="flex items-start gap-3">
        {onToggle && (
          <input
            type="checkbox"
            checked={ticked}
            onChange={onToggle}
            className="mt-1 accent-accent flex-shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_120px] gap-2 items-start">
            <input
              type="text"
              value={cat.display_name}
              onChange={(e) => onChange({ display_name: e.target.value })}
              className="px-2 py-1 bg-transparent border border-line rounded text-text text-[13px] font-medium outline-none focus:border-accent"
            />
            <div className="flex items-center gap-2">
              <Hash size={11} className="text-text-muted flex-shrink-0" />
              <input
                type="number"
                min={0}
                value={cat.required_qty}
                onChange={(e) => onChange({ required_qty: Math.max(0, parseInt(e.target.value || "0", 10)) })}
                className="w-16 px-2 py-1 bg-transparent border border-line rounded text-text text-[13px] font-mono outline-none focus:border-accent"
                placeholder="qty"
              />
              <span className="text-[11px] text-text-muted">required</span>
            </div>
          </div>
          {cat.description && (
            <div className="text-[11px] text-text-muted mt-1 line-clamp-2">{cat.description}</div>
          )}
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-[10px] font-mono uppercase tracking-wider2 text-text-dim">{cat.code}</span>
            {cat.is_per_instance && (
              <span className="text-[10px] font-mono uppercase tracking-wider2 text-accent">per-instance</span>
            )}
            {cat.is_custom && (
              <span className="text-[10px] font-mono uppercase tracking-wider2 text-warn">custom</span>
            )}
          </div>
        </div>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-text-muted hover:text-danger flex-shrink-0"
            title="Remove custom category"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Step 3 — Instances per per-instance category
// -----------------------------------------------------------------------------
function Step3Instances({
  perInstance, instances, setInstances,
}: {
  perInstance: SelectedCategory[];
  instances: Record<string, InstanceRow[]>;
  setInstances: (r: Record<string, InstanceRow[]>) => void;
}) {
  function add(catCode: string) {
    const next = { ...instances };
    const rows = next[catCode] ?? [];
    const prefix = AUTO_CODE_PREFIX[catCode];
    // T-411 — cabinet-family slots get the next fixed NBN code automatically
    next[catCode] = [...rows, { instance_code: prefix ? autoCode(prefix, rows.length) : "", instance_label: "" }];
    setInstances(next);
  }
  // T-420 — bulk paste (born of the Nullarbor Pack 5 rebuild: 169 slots typed
  // by hand, twice). Paste a comma/newline-separated list → one row per token.
  const [pasteFor, setPasteFor] = useState<string | null>(null);
  const [pasteText, setPasteText] = useState("");
  function applyPaste(catCode: string, mode: "replace" | "append") {
    const tokens = pasteText
      .split(/[\n,;]+/)
      .map(t => t.trim())
      .filter(Boolean);
    if (tokens.length === 0) return;
    const next = { ...instances };
    const fresh = tokens.map(t => ({ instance_code: t, instance_label: "" }));
    const existing = (next[catCode] ?? []).filter(r => r.instance_code.trim() || r.instance_label.trim());
    next[catCode] = mode === "replace" ? fresh : [...existing, ...fresh];
    setInstances(next);
    setPasteFor(null);
    setPasteText("");
  }
  function remove(catCode: string, idx: number) {
    const next = { ...instances };
    const prefix = AUTO_CODE_PREFIX[catCode];
    const kept = (next[catCode] ?? []).filter((_, i) => i !== idx);
    // T-411 — resequence so deleting BBS001 leaves BBS001 (not a lone BBS002)
    next[catCode] = prefix ? kept.map((r, k) => ({ ...r, instance_code: autoCode(prefix, k) })) : kept;
    setInstances(next);
  }
  function update(catCode: string, idx: number, patch: Partial<InstanceRow>) {
    const next = { ...instances };
    const rows = [...(next[catCode] ?? [])];
    rows[idx] = { ...rows[idx], ...patch };
    next[catCode] = rows;
    setInstances(next);
  }

  return (
    <div className="space-y-4">
      <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono">
        3. Name your photo slots — give each one a code/label so the crew knows what to capture
      </div>
      {perInstance.map(cat => {
        const rows = instances[cat.code] ?? [];

        // Strict per-instance categories (Sector/MW/AA) need codes like S1.
        // Non-strict ones (General with qty>1, Custom) get auto-numbered if
        // left blank, and the user can focus on writing meaningful labels.
        const isStrict = cat.is_per_instance;
        // T-411 — cabinet family: codes are FIXED NBN sequences (BBS001…),
        // shown as a chip, not typed.
        const autoPrefix = AUTO_CODE_PREFIX[cat.code];

        const codePlaceholder =
          cat.code === "sector"         ? "e.g. S1"  :
          cat.code === "microwave"      ? "e.g. MW1" :
          cat.code === "active_antenna" ? "e.g. AAU01" :
          isStrict                      ? "Code (required)" :
                                          `Auto: ${String(rows.length).padStart(2, "0")}`;

        const labelPlaceholder =
          cat.code === "sector"         ? "Optional label (e.g. north face)" :
          cat.code === "microwave"      ? "Optional — facing direction or remote site" :
          cat.code === "active_antenna" ? "Optional label" :
          autoPrefix                    ? "Optional — model / location (e.g. E6150)" :
                                          "What's this photo of? (e.g. Pole base, Tower top, Compound entry)";

        const helperText =
          cat.code === "sector"
            ? "One row per installed sector. Crew uploads the full subcategory list per sector."
            : cat.code === "microwave"
            ? "One row per microwave link — usually facing direction or remote site name."
            : cat.code === "active_antenna"
            ? "One row per active antenna installed."
            : autoPrefix
              ? `One row per unit — codes are fixed NBN sequence (${autoPrefix}001, ${autoPrefix}002…). Label is optional (model/location).`
            : isStrict
              ? "One row per occurrence — code required."
              : `${cat.required_qty} photo slot${cat.required_qty === 1 ? "" : "s"} for this category. The first box is the file ID (e.g. a member number like 258) — it appears in the client export filename. Leave it blank for the generic 01, 02, 03. The second box is a label so crew know what to shoot.`;

        return (
          <Card key={cat.code}>
            <div className="p-5">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <div className="text-text font-medium">
                    {cat.display_name}
                    {!isStrict && (
                      <span className="ml-2 text-[10px] text-text-dim font-mono uppercase">
                        qty {cat.required_qty}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-text-muted">{helperText}</div>
                </div>
                <div className="flex items-center gap-1.5">
                  {!autoPrefix && (
                    <Button variant="ghost" size="sm" type="button"
                      onClick={() => { setPasteFor(pasteFor === cat.code ? null : cat.code); setPasteText(""); }}>
                      Paste list
                    </Button>
                  )}
                  <Button variant="secondary" size="sm" icon={Plus} onClick={() => add(cat.code)} type="button">
                    Add slot
                  </Button>
                </div>
              </div>

              {/* T-420 — bulk paste: one code per line or comma-separated */}
              {pasteFor === cat.code && (
                <div className="mb-3 p-3 rounded border border-accent/30 bg-accent/5 space-y-2">
                  <textarea
                    value={pasteText}
                    onChange={e => setPasteText(e.target.value)}
                    rows={4}
                    autoFocus
                    placeholder={"Paste codes — one per line or comma-separated\ne.g. Member 74, Member 73, Member 63…"}
                    className="w-full bg-ink border border-line rounded px-2.5 py-2 text-[12.5px] font-mono text-text placeholder:text-text-dim"
                  />
                  <div className="flex items-center gap-2 text-[11px] text-text-muted">
                    <span>{pasteText.split(/[\n,;]+/).map(t => t.trim()).filter(Boolean).length} codes detected</span>
                    <div className="ml-auto flex gap-2">
                      <Button size="sm" variant="secondary" type="button" onClick={() => applyPaste(cat.code, "append")}>Append</Button>
                      <Button size="sm" variant="primary" type="button" onClick={() => applyPaste(cat.code, "replace")}>Replace all</Button>
                    </div>
                  </div>
                </div>
              )}

              {rows.length === 0 ? (
                <div className="text-[12px] text-text-muted italic">
                  No slots yet — tap "Add slot" to add one.
                </div>
              ) : (
                <div className="space-y-2">
                  {rows.map((r, i) => (
                    <div key={i} className="grid grid-cols-[120px_1fr_auto] gap-2 items-center">
                      {autoPrefix ? (
                        // T-411 — fixed NBN code, not editable
                        <div
                          className="px-2 py-1.5 bg-accent/10 border border-accent/30 rounded text-accent text-[13px] font-mono text-center select-none"
                          title="Fixed NBN sequence code"
                        >
                          {r.instance_code}
                        </div>
                      ) : (
                        <input
                          type="text"
                          value={r.instance_code}
                          onChange={(e) => update(cat.code, i, { instance_code: e.target.value })}
                          placeholder={isStrict ? codePlaceholder : `Auto: ${String(i + 1).padStart(2, "0")}`}
                          className="px-2 py-1.5 bg-ink-raised border border-line rounded text-text text-[13px] font-mono outline-none focus:border-accent"
                        />
                      )}
                      <input
                        type="text"
                        value={r.instance_label}
                        onChange={(e) => update(cat.code, i, { instance_label: e.target.value })}
                        placeholder={labelPlaceholder}
                        className="px-2 py-1.5 bg-ink-raised border border-line rounded text-text text-[13px] outline-none focus:border-accent"
                      />
                      <button
                        type="button"
                        onClick={() => remove(cat.code, i)}
                        className="text-text-muted hover:text-danger flex-shrink-0 p-1"
                        title="Remove slot"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Step 4 — Label prefix + title + notes
// -----------------------------------------------------------------------------
function Step4LabelTitle({
  labelPrefix, setLabelPrefix, title, setTitle, notes, setNotes,
  firstSubcategoryCode,
}: {
  labelPrefix: string; setLabelPrefix: (s: string) => void;
  title: string; setTitle: (s: string) => void;
  notes: string; setNotes: (s: string) => void;
  firstSubcategoryCode: string;
}) {
  return (
    <Card>
      <div className="p-5 space-y-4">
        <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono">
          4. Label prefix + pack identity
        </div>

        <Field
          label="Site label prefix *"
          hint="Front-end of every asset filename in this pack. Use the site code, RFNSA, or whatever the client expects on photo names."
        >
          <div className="relative">
            <Tag size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
            <Input
              value={labelPrefix}
              onChange={e => setLabelPrefix(e.target.value)}
              placeholder="e.g. SYD-001-NBN"
              className="pl-9 font-mono"
            />
          </div>
          {labelPrefix && (
            <div className="mt-2 text-[11px] text-text-muted">
              Preview: <span className="font-mono text-text">{labelPrefix}_{firstSubcategoryCode}_001.jpg</span>
            </div>
          )}
        </Field>

        <Field
          label="Pack title (optional)"
          hint="Defaults to the template name. Override if this pack covers a specific phase or subsystem."
        >
          <Input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g. Mobilisation handover"
          />
        </Field>

        <Field
          label="Notes for the crew (optional)"
          hint="Anything visible to anyone working this pack — special access, subsystem context, gotchas."
        >
          <Textarea
            rows={3}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Free-text notes for the crew."
          />
        </Field>
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Step 5 — Review summary
// -----------------------------------------------------------------------------
function Step5Review({
  template, selected, instances, labelPrefix, title, notes,
}: {
  template: TemplateOption | null;
  selected: SelectedCategory[];
  instances: Record<string, InstanceRow[]>;
  labelPrefix: string;
  title: string;
  notes: string;
}) {
  const totalInstances = Object.values(instances).reduce((a, r) => a + r.length, 0);

  return (
    <Card>
      <div className="p-5 space-y-4">
        <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono">
          5. Review + create
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Template"   value={template?.template_code ?? "—"} />
          <Stat label="Categories" value={String(selected.length)} />
          <Stat label="Instances"  value={String(totalInstances)} />
          <Stat label="Prefix"     value={labelPrefix || "—"} mono />
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono mb-2">
            Categories ({selected.length})
          </div>
          <div className="flex flex-wrap gap-1.5">
            {selected.map(c => (
              <span key={c.code} className="px-2 py-0.5 rounded bg-ink-raised border border-line text-[11px] text-text">
                {c.display_name}
                {c.required_qty > 0 && <span className="ml-1 font-mono text-text-muted">×{c.required_qty}</span>}
                {c.is_per_instance && <span className="ml-1 text-[9px] uppercase font-mono text-accent">per-inst</span>}
                {c.is_custom && <span className="ml-1 text-[9px] uppercase font-mono text-warn">custom</span>}
              </span>
            ))}
          </div>
        </div>

        {totalInstances > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono mb-2">
              Instances
            </div>
            <div className="space-y-1.5">
              {Object.entries(instances).map(([catCode, rows]) =>
                rows.length === 0 ? null : (
                  <div key={catCode} className="text-[12px]">
                    <span className="text-text-muted">{catCode}: </span>
                    <span className="text-text font-mono">
                      {rows.map(r => r.instance_code).join(", ")}
                    </span>
                  </div>
                )
              )}
            </div>
          </div>
        )}

        {(title || notes) && (
          <div>
            {title && (
              <div className="text-[12px]">
                <span className="text-text-muted">Title: </span>
                <span className="text-text">{title}</span>
              </div>
            )}
            {notes && (
              <div className="text-[12px] mt-1">
                <span className="text-text-muted">Notes: </span>
                <span className="text-text whitespace-pre-wrap">{notes}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

function Stat({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="p-3 rounded bg-ink-raised border border-line">
      <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono mb-1">{label}</div>
      <div className={`text-[14px] font-medium ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
